import { Injectable, inject } from '@angular/core'
import { ProductCategory } from '@tradecafe/types/core'
import { pick } from 'lodash-es'
import { AuthApiService } from 'src/api/auth'
import { ProductCategoryApiService } from 'src/api/product/category'

const ALLOWED_FIELDS: Array<keyof ProductCategory> = ['name', 'type', 'attributes', 'department_id']
const CACHE_MAX_AGE = 3600 * 1000 // 1hr


/**
 * ProductCategories service
 *
 * @export
 * @returns
 */
@Injectable()
export class ProductCategoriesService {
  private AuthApi = inject(AuthApiService)
  private ProductCategoryApi = inject(ProductCategoryApiService)



  private cacheExpiration = 0
  private cache = Promise.resolve({ data: [] })


  /**
   * Get all available product-types
   */
  async getProductCategories() {
    if (!this.cache || this.cacheExpiration < Date.now()) {
      this.cacheExpiration = Date.now() + CACHE_MAX_AGE // 1 min
      // NOTE: we fetch everything
      this.cache = this.ProductCategoryApi.list({ limit: Number.MAX_SAFE_INTEGER })
      .then(async (productCategories) => {
        productCategories.data.forEach((productCategory: any) => {
          productCategory.created = productCategory.created || productCategory.createdAt
          productCategory.updated = productCategory.updatedAt || productCategory.updated || productCategory.created
        })
        return productCategories
      })
      .catch((err) => {
        this.invalidateCache()
        throw err
      })
    }
    const { data } = await this.cache
    return data
  }


  /**
   * Create new productCategory
   *
   * @param {any} productCategory
   * @returns
   */
  async create(productCategory: Partial<ProductCategory>) {
    const payload = pick(productCategory, ALLOWED_FIELDS)
    const { user_id } = this.AuthApi.currentUser
    payload.user_id = user_id

    const { data } = await this.ProductCategoryApi.create(payload)
    this.invalidateCache()
    return data
  }

  /**
   * Update productCategory
   *
   * @param {any} productCategory
   * @returns
   */
  async update(productCategory: Partial<ProductCategory>) {
    const { category_id } = productCategory
    const payload = pick(productCategory, ALLOWED_FIELDS)
    const { user_id } = this.AuthApi.currentUser
    payload.user_id = user_id

    const { data } = await this.ProductCategoryApi.update(category_id, payload)
    this.invalidateCache()
    return data
  }

  /**
   * Invalidate product-types in-memory cache
   *
   * @private
   */
  private invalidateCache() {
    this.cacheExpiration = 0
  }
}
